<template>
    <MarkerContainer
        isStamp
        :busy="!stampShift.id || stampShift.updating"
        :fromOtherRoster="fromOtherRoster"
        :toOtherRoster="shift.rosterId !== rosterId"
        :readonly="!editable"
        :availabilityConflicted="hasWishConflict || hasAvailabilityConflict"
        :markingColorId="shift.template!.colorId"

        @click:marker="changeShift"
        @contextmenu.prevent.stop="startContextMenu"
        @touchstart.passive.stop="startTouch"
        @touchmove.passive.stop="mobileContextMenu.moveTouch"
        @touchend.passive.stop="mobileContextMenu.endTouch"
    >
        <!-- give shifttId and get back label to render in chip -->
        {{ shiftLabel }}
        <DeploymentMarkerRosterName v-if="isShiftFromOtherRoster">
            ({{ rosterName }})
        </DeploymentMarkerRosterName>
    </MarkerContainer>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import {Shift, Company, PersonalPlanningWish, Unavailability, PersonalStampShift} from '@cquential/api';
    import {RosterDay, ScreenCoordinates} from '@/types/types';
    import {ResourceWithKeyedByDateObjects} from '@/types/api-object-extensions';
    import useMobileContextMenu from '@/util/useMobileContextMenu';
    import WishUtil from '@/util/WishUtil';
    import AvailabilityUtil from '@/util/AvailabilityUtil';
    import StampShiftEditModal from '@/components/modals/StampShiftEditModal.vue';
    import Planbord from '@/components/Planbord.vue';
    import {MobileContextMenu} from '@/components/types';
    import DeploymentMarkerRosterName from '@/components/Planbord/DeploymentMarkerRosterName.vue';
    import MarkerContainer from '@/components/Planbord/MarkerContainer.vue';
    import DeploymentCellContextMenu from '@/components/Planbord/DeploymentCellContextMenu.vue';

    @Component({
        components: {
            DeploymentMarkerRosterName,
            MarkerContainer,
        },
    })
    export default class StampShiftMarker extends Vue {
        @Prop({required: true}) public stampShift!: PersonalStampShift;
        @Prop({required: true}) public shift!: Shift;
        @Prop({required: true}) public day!: RosterDay;
        @Prop({required: true}) public resource!: ResourceWithKeyedByDateObjects;
        @Prop() public company?: Company;
        @Prop({type: Number, required: true}) public rosterId!: number;
        @Prop({type: Number}) public shiftIndex?: number;
        @Prop({type: Boolean}) public fromOtherRoster!: boolean;
        @Prop({type: Boolean}) public editable!: boolean;
        @Prop() public wish?: PersonalPlanningWish;
        @Prop({required: true}) public unavailabilities!: Unavailability[];
        @Prop({required: true}) public rosterNames!: Record<number, string>;
        @Prop({type: Number, required: true}) public horizontalPosition!: number;
        @Prop({type: Number, required: true}) public verticalPosition!: number;

        private mobileContextMenu: MobileContextMenu = useMobileContextMenu();

        public startContextMenu(): void {
            if (this.mobileContextMenu.isTargetedDevice.value) {
                return;
            }
            this.openStampShiftEditModal();
        }

        public startTouch(event: TouchEvent): void {
            const promise: Promise<ScreenCoordinates> | null = this.mobileContextMenu.startTouch(event);
            if (promise) {
                promise.then(this.openStampShiftEditModal).catch((): void => {
                    return;
                });
            }
        }

        public changeShift(): void {
            /** @see Planbord.setActiveCell() */
            // @ts-expect-error TS2339
            Planbord.__vfdConstructor.setActiveCell(this.horizontalPosition, this.verticalPosition, true, false, this.shiftIndex);
        }

        private openStampShiftEditModal(): void {
            this.closeContextMenus();
            if (!('id' in this.stampShift)) {
                return;
            }

            /** @see StampShiftEditModal.openModal() */
            // @ts-expect-error TS2339
            StampShiftEditModal.__vfdConstructor.openModal(this.resource, null, this.stampShift, this.day.date);
        }

        private closeContextMenus(): void {
            /** @see DeploymentCellContextMenu.hideMenu() */
            // @ts-expect-error TS2339
            DeploymentCellContextMenu.__vfdConstructor.hideMenu();
        }

        public get shiftLabel(): string {
            if (!this.shift.template!.code) {
                return 'UNKNOWN';
            }

            return this.shift.template!.code;
        }

        public get hasWishConflict(): boolean {
            return (this.wish && WishUtil.hasWishConflicts(this.wish, this.shift.id, this.shift.beginDate, this.shift.endDate)) || false;
        }

        public get hasAvailabilityConflict(): boolean {
            return AvailabilityUtil.hasShiftConflict(this.unavailabilities, this.day.date, this.shift);
        }

        public get isShiftFromOtherRoster(): boolean {
            return this.shift.rosterId !== this.rosterId;
        }

        public get rosterName(): string {
            return this.rosterNames[this.shift.rosterId] ?? 'Vervallen rooster';
        }
    }
</script>
